// Routines list. Lets the user browse saved routines, create new ones, edit
// them, launch them as a workout, or delete them.
import type { Routine, RoutineExercise, Set as WorkoutSet } from '@onflow/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Chip, ChipRow } from '../components/v2/Chip.js';
import { ConfirmDialog } from '../components/v2/ConfirmDialog.js';
import { EmptyState } from '../components/v2/EmptyState.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowCard, FlowLabel } from '../components/v2/FlowCard.js';
import { PageHeader } from '../components/v2/PageHeader.js';
import {
  useCopyRoutineTemplate,
  useDeleteRoutine,
  useMarkRoutineUsed,
  useRoutines,
  useRoutineTemplates,
} from '../hooks/useRoutines.js';
import { timeAgo } from '../lib/format.js';
import { toast } from '../lib/toast.js';
import { useActiveWorkoutStore } from '../stores/activeWorkoutStore.js';

// Translates a routine into the in-memory active-workout shape: each
// targetSets becomes one empty `normal` set carrying the routine's target
// weight + reps as defaults.
function routineToWorkoutExercises(
  routine: Routine,
): { exerciseId: string; notes: string; restTimerSeconds: number; sets: WorkoutSet[] }[] {
  return routine.exercises.map((re: RoutineExercise) => {
    const sets: WorkoutSet[] = Array.from({ length: re.targetSets }, (_, i) => ({
      setNumber: i + 1,
      type: 'normal',
      weight: re.targetWeight ?? 0,
      reps: re.targetReps ?? 0,
      completed: false,
    }));
    return {
      exerciseId: re.exerciseId,
      notes: '',
      restTimerSeconds: re.restTimerSeconds ?? 90,
      sets,
    };
  });
}

export function Routines() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useRoutines();
  const deleteMutation = useDeleteRoutine();
  const templates = useRoutineTemplates();
  const copyTemplate = useCopyRoutineTemplate();
  const markUsed = useMarkRoutineUsed();
  const startActive = useActiveWorkoutStore((s) => s.start);

  const routines = useMemo(() => data ?? [], [data]);

  // Group routines into their split folders. A folder ("PPL") holds multiple
  // day-routines ("Push", "Pull", "Legs"); routines with no folder fall into a
  // trailing "Standalone" bucket. Folders sort alphabetically; standalone last.
  const groups = useMemo(() => {
    const byFolder = new Map<string, Routine[]>();
    for (const r of routines) {
      const key = r.folder ?? '';
      const list = byFolder.get(key);
      if (list) list.push(r);
      else byFolder.set(key, [r]);
    }
    const named = Array.from(byFolder.keys())
      .filter((k) => k !== '')
      .sort();
    const ordered = [...named, ...(byFolder.has('') ? [''] : [])];
    return ordered.map((folder) => ({ folder, items: byFolder.get(folder)! }));
  }, [routines]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Routine | null>(null);

  const toggleFolder = (folder: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });

  const addDayToFolder = (folder: string) =>
    navigate(`/routines/new?folder=${encodeURIComponent(folder)}`);

  const handleStart = (routine: Routine) => {
    const workoutExercises = routineToWorkoutExercises(routine);
    startActive({ title: routine.name, exercises: workoutExercises, routineId: routine.id });
    // Fire-and-forget — last-used is not load-bearing for the workout flow,
    // and a network error here shouldn't block starting the session.
    markUsed.mutate(routine.id);
    navigate('/workout/active');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteMutation.mutateAsync(target.id);
      toast.success('Routine deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const header = (
    <PageHeader
      eyebrow="Train"
      title="Routines"
      action={
        <FlowButton variant="primary" size="sm" onClick={() => navigate('/routines/new')}>
          Create
        </FlowButton>
      }
    />
  );

  if (isLoading) {
    return (
      <section className="fade-in">
        {header}
        <EmptyState title="Loading routines…" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="fade-in">
        {header}
        <EmptyState
          tone="error"
          role="alert"
          title="Couldn't load routines"
          body={(error as Error).message}
        />
      </section>
    );
  }

  return (
    <section className="fade-in">
      {header}

      {(templates.data?.items.length ?? 0) > 0 && (
        <FlowCard style={{ marginBottom: 20 }}>
          <FlowLabel>Start from a template</FlowLabel>
          <p style={{ margin: '8px 0 12px', fontSize: 13, color: 'var(--ink-2)' }}>
            Copy a weekly split into your routines, then choose the exercises yourself.
          </p>
          <ChipRow>
            {templates.data!.items.map((template) => (
              <Chip
                key={template.id}
                disabled={copyTemplate.isPending}
                onClick={() =>
                  copyTemplate.mutate(template.id, {
                    onSuccess: ({ routines: copied }) =>
                      toast.success(`Copied ${copied.length} editable workouts`),
                    onError: (err) =>
                      toast.error(err instanceof Error ? err.message : 'Copy failed'),
                  })
                }
              >
                {template.name} · {template.daysPerWeekMin} days
              </Chip>
            ))}
          </ChipRow>
        </FlowCard>
      )}

      {routines.length === 0 ? (
        <EmptyState
          icon="dumbbell"
          title="No routines yet"
          body="Save your favorite workouts as routines and launch them with one tap."
          action={
            <FlowButton variant="primary" onClick={() => navigate('/routines/new')}>
              Create your first routine
            </FlowButton>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {groups.map(({ folder, items }) => {
            const isStandalone = folder === '';
            const isCollapsed = collapsed.has(folder);
            return (
              <div key={folder || '__standalone__'}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleFolder(folder)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      cursor: 'pointer',
                      color: 'var(--ink-1)',
                      minWidth: 0,
                    }}
                    aria-expanded={!isCollapsed}
                  >
                    <span
                      aria-hidden
                      style={{
                        display: 'inline-block',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                        transition: 'transform var(--t-state) var(--ease-out)',
                        color: 'var(--ink-3)',
                        fontSize: 12,
                      }}
                    >
                      ▾
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>
                      {isStandalone ? 'Standalone' : folder}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                      {items.length} day{items.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  {!isStandalone && (
                    <FlowButton variant="quiet" size="sm" onClick={() => addDayToFolder(folder)}>
                      + Add day
                    </FlowButton>
                  )}
                </div>

                {!isCollapsed && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {items.map((r) => (
                      <FlowCard key={r.id}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-1)' }}>
                          {r.name}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                          {r.exercises.length} exercise{r.exercises.length === 1 ? '' : 's'} ·{' '}
                          {r.lastUsed ? `last used ${timeAgo(r.lastUsed)}` : 'never used'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <FlowButton variant="soft" size="sm" onClick={() => handleStart(r)}>
                            Start
                          </FlowButton>
                          <FlowButton
                            variant="quiet"
                            size="sm"
                            onClick={() => navigate(`/routines/edit/${r.id}`)}
                          >
                            Edit
                          </FlowButton>
                          <FlowButton
                            variant="danger"
                            size="sm"
                            disabled={deleteMutation.isPending}
                            onClick={() => setDeleteTarget(r)}
                          >
                            Delete
                          </FlowButton>
                        </div>
                      </FlowCard>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete routine?"
        message={`"${deleteTarget?.name ?? ''}" will be removed. This can't be undone.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        confirmVariant="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
